import { observer } from 'mobx-react-lite'
import React, { useContext } from 'react'
import { Form, Formik } from 'formik'
import Input from '../../../../FormComponents/Input'
import Campaign from '../../../../../models/Campaign';
import { ArrowLeft, Person } from 'react-bootstrap-icons'
import { CampaignContext } from '../../../../../contexts/CampaignContext'
import axiosService from '../../../../../utils/AxiosService'
import { toastError, toastSuccess } from '../../../../FormComponents/Toasts'
import Modal from '../../../../Modal/Modal'
import CurrencySelect from '../../../../FormComponents/CurrencySelect'
import { CampaignOverviewTargetsSchema } from '../../../../../schemas/CampaignTargetSchema';


export default observer(function CampaignTargetsUpdateModal({ isOpen, setIsOpen }) {
  const { campaign, campaignState } = useContext(CampaignContext)

  const onSubmit = (values) => {
    axiosService.patch(`/api/project_manager/campaigns/${campaign.id}`, { campaign: values })
      .then(response => {
        const campaignData = {
          ...response,
          client_id: response.client?.id
        }
        campaignState.campaign = new Campaign(campaignData)
        toastSuccess("Campaign updated successfully")
        setIsOpen(false)
      })
      .catch(error => {
        console.error('Update error:', error)
        toastError(error?.data?.error || "Failed to update campaign")
      })
  }

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <Modal.Body>
        <div className="p-2 h-full flex flex-col" style={{ maxHeight: '80vh' }}>
          <div className="flex justify-between items-center pb-4 sticky top-0 bg-white z-10">
            <h2 className='font-bold text-2xl'>Campaign targets</h2>
            <Modal.DissmissButton className='flex items-center p-2 rounded-lg hover:bg-gray-200'>
              <ArrowLeft className='mr-1 font-bold' /><span className='font-bold'>Back</span>
            </Modal.DissmissButton>
          </div>
          <div className="flex-1 overflow-y-auto">
            <Formik
              initialValues={{
                gross_reach_target: campaign.gross_reach_target,
                net_reach_target: campaign.net_reach_target,
                impressions_target: campaign.impressions_target,
                influencer_target: campaign.influencer_target,
                action_count_target: campaign.action_count_target,
                currency: campaign.currency,
                currency_symbol: campaign.currency_symbol,
                influencer_budget: campaign.influencer_budget,
                social_budget: campaign.social_budget,
                media_budget: campaign.media_budget,
                travel_cost_target: campaign.travel_cost_target,
                attendance_cost_target: campaign.attendance_cost_target,
              }}
              validationSchema={CampaignOverviewTargetsSchema}
              onSubmit={onSubmit}
            >
              {({ handleBlur, handleChange, values, touched, errors, setFieldValue }) => (
                <Form>
                  <div className="w-full flex flex-col gap-3">
                    <div className="flex flex-col">
                      <div className="flex gap-4">
                        <div>
                          <Input overrideClassName="pl-4" error={touched.gross_reach_target && errors.gross_reach_target} noLabelPadding label="Gross Reach" placeholder="Gross Reach" name='gross_reach_target' onChange={handleChange} onBlur={handleBlur} value={values.gross_reach_target} />
                        </div>
                        <div>
                          <Input overrideClassName="pl-4" error={touched.net_reach_target && errors.net_reach_target} noLabelPadding label="Net Reach" placeholder="Net Reach" name='net_reach_target' onChange={handleChange} onBlur={handleBlur} value={values.net_reach_target} />
                        </div>
                      </div>
                      <div className="flex justify-between gap-4">
                        <div>
                          <Input overrideClassName="pl-4" error={touched.impressions_target && errors.impressions_target} noLabelPadding label="Impressions" placeholder="Target Impressions" name='impressions_target' onChange={handleChange} onBlur={handleBlur} value={values.impressions_target} />
                        </div>
                        <div className='relative w-[204px]'>
                          <Input
                            error={touched.influencer_target && errors.influencer_target}
                            addon={<Person />}
                            addonPosition="end"
                            noLabelPadding
                            label="Influencers"
                            placeholder="Influencer count"
                            name="influencer_target"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.influencer_target}
                          />
                        </div>
                      </div>
                    </div>
                    <div className='relative'>
                      <Input
                        error={touched.action_count_target && errors.action_count_target}
                        noLabelPadding
                        label="Action Count Target"
                        placeholder="Target number of actions"
                        name="action_count_target"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.action_count_target}
                      />
                    </div>
                    <div>
                      <CurrencySelect
                        error={touched.currency && errors.currency}
                        isClearable
                        name="currency"
                        label="Currency"
                        onChange={(option) => {
                          setFieldValue('currency', option.value, true)
                          setFieldValue('currency_symbol', option.symbol)
                        }}
                        onBlur={handleBlur}
                        value={values.currency}
                        noLabelPadding
                      />
                    </div>
                    <div className='relative'>
                      <Input
                        addon={campaign.currency_symbol}
                        addonPosition="start"
                        formatNumber
                        className='px-2'
                        error={touched.influencer_budget && errors.influencer_budget}
                        noLabelPadding
                        label="Influencer Budget"
                        placeholder="Influencer budget"
                        name="influencer_budget"
                        onChange={(e) => {
                          const value = e.target.value.replace(/,/g, '.')
                          setFieldValue('influencer_budget', value, true)
                        }}
                        onBlur={handleBlur}
                        value={values.influencer_budget}
                      />
                    </div>
                    <div className='relative'>
                      <Input
                        addon={campaign.currency_symbol}
                        addonPosition="start"
                        className='px-2'
                        error={touched.social_budget && errors.social_budget}
                        noLabelPadding
                        label="Social Budget"
                        placeholder="Social ads budget"
                        name="social_budget"
                        onChange={(e) => {
                          const value = e.target.value.replace(/,/g, '.')
                          setFieldValue('social_budget', value, true)
                        }}
                        onBlur={handleBlur}
                        value={values.social_budget}
                      />
                    </div>
                    <div className='relative'>
                      <Input
                        addon={campaign.currency_symbol}
                        addonPosition="start"
                        className='px-2'
                        error={touched.media_budget && errors.media_budget}
                        noLabelPadding
                        label="Buyout Budget"
                        placeholder="Buyout Budget"
                        name="media_budget"
                        onChange={(e) => {
                          const value = e.target.value.replace(/,/g, '.')
                          setFieldValue('media_budget', value, true)
                        }}
                        onBlur={handleBlur}
                        value={values.media_budget}
                      />
                    </div>
                    {campaign.travel_cost_enabled && (
                      <div className='relative'>
                        <Input
                          addon={campaign.currency_symbol}
                          addonPosition="start"
                          className='px-2'
                          error={touched.travel_cost_target && errors.travel_cost_target}
                          noLabelPadding
                          label="Travel Costs Target"
                          placeholder="Travel costs budget"
                          name="travel_cost_target"
                          onChange={(e) => {
                            const value = e.target.value.replace(/,/g, '.')
                            setFieldValue('travel_cost_target', value, true)
                          }}
                          onBlur={handleBlur}
                          value={values.travel_cost_target}
                        />
                      </div>
                    )}
                    {campaign.attendance_cost_enabled && (
                      <div className='relative'>
                        <Input
                          addon={campaign.currency_symbol}
                          addonPosition="start"
                          className='px-2'
                          error={touched.attendance_cost_target && errors.attendance_cost_target}
                          noLabelPadding
                          label="Event Attendance Target"
                          placeholder="Event attendance budget"
                          name="attendance_cost_target"
                          onChange={(e) => {
                            const value = e.target.value.replace(/,/g, '.')
                            setFieldValue('attendance_cost_target', value, true)
                          }}
                          onBlur={handleBlur}
                          value={values.attendance_cost_target}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between mt-7">
                    <Modal.DissmissButton className='rb-btn-cancel font-bold text-lg ml p-2 px-4 rounded-lg'>
                      <span className='font-bold'>Cancel</span>
                    </Modal.DissmissButton>

                    <button
                      type="submit"
                      className="ml-auto bg-rb-green text-white font-bold rounded-lg p-2 px-4 submit text-center active:shadow-outer active:shadow-rb-green border-rb-green"
                    >
                      Save
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
})
